/* Rendición de Gastos — utilidades compartidas de esquema dinámico.
 * Debe reflejar la lógica del backend (backend/gastos.py) para dar cálculo en
 * vivo; el backend siempre recalcula al guardar (fuente de verdad). */

export const FIELD_TYPES = [
  { value: "texto", label: "Texto" },
  { value: "textarea", label: "Texto largo" },
  { value: "numero", label: "Número" },
  { value: "monto", label: "Monto (CLP)" },
  { value: "fecha", label: "Fecha" },
  { value: "direccion", label: "Dirección" },
  { value: "select", label: "Lista desplegable" },
  { value: "imagen", label: "Imagen (comprobante)" },
  { value: "distancia", label: "Distancia (km entre 2 direcciones)" },
  { value: "calculo", label: "Cálculo (fórmula)" },
];

export const fieldTypeLabel = (t) =>
  (FIELD_TYPES.find((x) => x.value === t) || {}).label || t;

export function formatCLP(n) {
  const v = Number(n || 0);
  return "$" + Math.round(v).toLocaleString("es-CL");
}

export function formatNumber(n) {
  const v = Number(n || 0);
  return v.toLocaleString("es-CL", { maximumFractionDigits: 2 });
}

/** Coerce a número, tolerando formato es-CL. Debe reflejar _to_number del backend. */
export function toNumber(v) {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return v;
  let s = String(v).trim().replace(/\$/g, "").replace(/\s/g, "");
  if (s.includes(",") && s.includes(".")) s = s.replace(/\./g, "").replace(",", ".");
  else if (s.includes(",")) s = s.replace(",", ".");
  else if ((s.match(/\./g) || []).length > 1) s = s.replace(/\./g, "");
  const n = parseFloat(s);
  return isFinite(n) ? n : 0;
}

export const camposByKey = (categoria) => {
  const out = {};
  (categoria?.campos || []).forEach((c) => { if (c.key) out[c.key] = c; });
  return out;
};

export const constsOf = (categoria) => {
  const out = {};
  (categoria?.constantes || []).forEach((k) => {
    if (k.key != null) out[`const:${k.key}`] = toNumber(k.valor);
  });
  return out;
};

/** Evalúa una fórmula tipo "{distancia} * {const:costo_por_km}" (solo UX). */
export function evalFormula(formula, variables) {
  if (!formula) return 0;
  const expr = String(formula).replace(/\{([^}]+)\}/g, (_, k) => {
    const v = Number(variables[k.trim()] || 0);
    return isFinite(v) ? String(v) : "0";
  });
  if (!/^[0-9+\-*/%.()\s]*$/.test(expr)) return 0;
  try {
    // eslint-disable-next-line no-new-func
    const r = Function('"use strict";return (' + (expr || "0") + ")")();
    return isFinite(r) ? r : 0;
  } catch {
    return 0;
  }
}

/** Recalcula campos 'calculo' y el monto del ítem. Refleja compute_item del backend. */
export function computeItemLocal(categoria, valores) {
  const vals = { ...(valores || {}) };
  const campos = camposByKey(categoria);
  const variables = {};
  Object.values(campos).forEach((c) => {
    if (["numero", "monto", "distancia"].includes(c.tipo)) variables[c.key] = toNumber(vals[c.key]);
  });
  Object.assign(variables, constsOf(categoria));

  Object.values(campos)
    .sort((a, b) => (a.orden || 0) - (b.orden || 0))
    .forEach((c) => {
      if (c.tipo === "calculo") {
        const val = evalFormula((c.config || {}).formula || "", variables);
        vals[c.key] = Math.round(val * 100) / 100;
        variables[c.key] = val;
      }
    });

  const cm = categoria?.campo_monto;
  let monto = 0;
  if (cm && campos[cm]) monto = toNumber(vals[cm]);
  else {
    monto = Object.values(campos)
      .filter((c) => ["monto", "calculo"].includes(c.tipo))
      .reduce((a, c) => a + toNumber(vals[c.key]), 0);
  }
  return { valores: vals, monto: Math.round(monto * 100) / 100 };
}

export function rendicionTotal(categoriasById, items) {
  return (items || []).reduce((acc, it) => {
    const cat = categoriasById[it.categoria_id];
    if (!cat) return acc + toNumber(it.monto);
    return acc + computeItemLocal(cat, it.valores || {}).monto;
  }, 0);
}

export function missingRequired(categoria, valores) {
  const miss = [];
  (categoria?.campos || []).forEach((c) => {
    if (!c.obligatorio || c.tipo === "calculo") return;
    const v = (valores || {})[c.key];
    const empty = v == null || v === "" || (Array.isArray(v) && v.length === 0);
    if (empty) miss.push(c.label || c.key);
  });
  return miss;
}

export const imageSrc = (apiBase, id) => `${apiBase}/api/gastos/images/${id}`;

/* ---- Ciclo de vida de la rendición (refleja ESTADOS_RENDICION del backend) ---- */
export const ESTADOS_RENDICION = {
  borrador:    { label: "Borrador",          badge: "bg-amber-50 text-amber-700" },
  enviada:     { label: "Enviada",           badge: "bg-blue-50 text-blue-700" },
  observada:   { label: "Con observaciones", badge: "bg-purple-50 text-purple-700" },
  aprobada:    { label: "Aprobada",          badge: "bg-emerald-50 text-emerald-700" },
  rechazada:   { label: "Rechazada",         badge: "bg-red-50 text-red-700" },
  reembolsada: { label: "Reembolsada",       badge: "bg-teal-50 text-teal-700" },
};

export const estadoInfo = (e) =>
  ESTADOS_RENDICION[e === "completado" ? "enviada" : e] || ESTADOS_RENDICION.borrador;

export const ESTADOS_EDITABLES = ["borrador", "observada"];
